import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { useAdminAuth } from '../utils/adminAuth';
import { useTranslation } from 'react-i18next';
import { useCapability } from '../utils/capabilityContext';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import { getActiveWorkspace } from '../utils/workspace';

const TopNavbar = () => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const { isAdminAuthenticated, logoutAdmin } = useAdminAuth();
  const { refresh: refreshCapabilities } = useCapability();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeWorkspace = getActiveWorkspace(location.pathname);
  const showAdminIdentity = activeWorkspace === 'admin' && isAdminAuthenticated;
  const showDoctorIdentity = activeWorkspace === 'doctor' && currentUser;
  const showHomeIdentity = activeWorkspace === 'home' && (currentUser || isAdminAuthenticated);
  const shouldShowUserChip = showAdminIdentity || showDoctorIdentity || showHomeIdentity;
  const chipLabel = showAdminIdentity
    ? t('dashboard.admin')
    : showDoctorIdentity
      ? currentUser?.name
      : currentUser?.name || (isAdminAuthenticated ? t('dashboard.admin') : '');
  const primaryLogoutIsAdmin = activeWorkspace === 'admin' || (activeWorkspace === 'home' && isAdminAuthenticated && !currentUser);

  React.useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleDoctorLogout = async () => {
    await logout();
    navigate('/login');
    setUserMenuOpen(false);
  };

  const handleAdminLogout = async () => {
    logoutAdmin();
    await refreshCapabilities();
    navigate('/admin-login');
    setUserMenuOpen(false);
  };

  return (
    <>
      <header className="app-topbar">
        <div className="app-topbar__actions utility-actions">
          <ThemeSwitcher />
          <LanguageSwitcher />

          {shouldShowUserChip && (
            <div className="user-chip">
              <button
                type="button"
                className="user-chip__trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <span className="user-chip__avatar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <span>
                  {chipLabel}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={userMenuOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="dropdown-panel">
                  {(activeWorkspace !== 'admin' && currentUser) && (
                    <button
                      type="button"
                      className="dropdown-panel__item"
                      onClick={() => {
                        navigate('/user-center');
                        setUserMenuOpen(false);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span>{t('dashboard.userCenter')}</span>
                    </button>
                  )}
                  {activeWorkspace === 'home' && isAdminAuthenticated && (
                    <button
                      type="button"
                      className="dropdown-panel__item"
                      onClick={() => {
                        navigate('/admin');
                        setUserMenuOpen(false);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <span>{t('dashboard.admin')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="dropdown-panel__item dropdown-panel__item--danger"
                    onClick={primaryLogoutIsAdmin ? handleAdminLogout : handleDoctorLogout}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16,17 21,12 16,7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>{primaryLogoutIsAdmin ? t('dashboard.logoutAdmin') : t('auth.logout')}</span>
                  </button>
                  {activeWorkspace === 'home' && isAdminAuthenticated && currentUser && (
                    <button
                      type="button"
                      className="dropdown-panel__item dropdown-panel__item--danger"
                      onClick={handleAdminLogout}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16,17 21,12 16,7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      <span>{t('dashboard.logoutAdmin')}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {userMenuOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 8
          }}
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </>
  );
};

export default TopNavbar;
